import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link as LinkIcon, X, ArrowUp, ArrowDown, Plus } from "lucide-react";
import { api, apiError, imgSrc } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]";

function uploadLabel(busy, single) {
  if (busy) return "Uploading…";
  return single ? "Upload Photo" : "Upload Images";
}

export const ImagePicker = ({ value = [], onChange, max = 12, single = false, label = "Images", testId = "image-picker" }) => {
  const fileRef = useRef(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const list = single ? [value].filter(Boolean) : value;
  const emit = (next) => onChange(single ? (next[0] || "") : next);

  const addUrls = (urls) => {
    const next = single ? urls.slice(0, 1) : [...list, ...urls].slice(0, max);
    emit(next);
  };

  const upload = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of arr.slice(0, single ? 1 : max)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      addUrls(urls);
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addLink = () => {
    const u = link.trim();
    if (!/^https?:\/\/\S+/i.test(u)) { toast.error("Enter a valid image link starting with http(s)://"); return; }
    addUrls([u]);
    setLink("");
  };

  const move = (i, d) => { const n = [...list]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; emit(n); };
  const remove = (i) => emit(list.filter((_, k) => k !== i));

  return (
    <div data-testid={testId}>
      <div className="mb-1 flex items-center justify-between"><label className="text-xs font-medium text-[var(--text-muted)]">{label}</label><span className="text-[11px] text-[var(--text-muted)]">{list.length}{single ? "" : ` / ${max}`}</span></div>
      {list.length > 0 && (
        <div className={`mb-3 grid gap-2 ${single ? "grid-cols-1 max-w-[160px]" : "grid-cols-3 sm:grid-cols-4"}`}>
          {list.map((u, i) => (
            <div key={`${u}-${i}`} className="group relative overflow-hidden rounded-lg border border-[var(--border-subtle)]" data-testid={`${testId}-item-${i}`}>
              <img src={imgSrc(u)} alt="" className="aspect-square w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
              {i === 0 && !single && <span className="absolute left-1 top-1 rounded bg-[var(--accent-gold)] px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[#071711]">Cover</span>}
              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1 opacity-0 transition group-hover:opacity-100">
                {!single ? <div className="flex gap-0.5"><button type="button" onClick={() => move(i, -1)} className="rounded p-0.5 text-white hover:text-[var(--accent-gold)]"><ArrowUp className="h-3.5 w-3.5" /></button><button type="button" onClick={() => move(i, 1)} className="rounded p-0.5 text-white hover:text-[var(--accent-gold)]"><ArrowDown className="h-3.5 w-3.5" /></button></div> : <span />}
                <button type="button" onClick={() => remove(i)} data-testid={`${testId}-remove-${i}`} className="rounded p-0.5 text-white hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
              </div>
              {u.startsWith("/api/") && <span className="absolute right-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[9px] text-white">Uploaded</span>}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-2 sm:flex-row">
        <input ref={fileRef} type="file" accept="image/*" multiple={!single} className="hidden" onChange={(e) => upload(e.target.files)} data-testid={`${testId}-file`} />
        <button type="button" disabled={busy || (!single && list.length >= max)} onClick={() => fileRef.current?.click()} data-testid={`${testId}-upload-btn`} className="btn-outline-gold !px-4 !py-2 text-xs disabled:opacity-50"><Upload className="h-4 w-4" /> {uploadLabel(busy, single)}</button>
        <div className="flex flex-1 gap-2">
          <div className="relative flex-1"><LinkIcon className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]" /><input value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }} placeholder="Paste image link (https://…)" data-testid={`${testId}-link`} className={`${inputCls} pl-9`} /></div>
          <button type="button" onClick={addLink} data-testid={`${testId}-add-link`} className="btn-gold !px-3 !py-2 text-xs"><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">Upload JPG/PNG/WEBP up to 8 MB (stored securely in Archedia storage), or paste a direct image link from any public source (e.g. Unsplash, developer website, Google Drive public link).</p>
    </div>
  );
};

export default ImagePicker;
